/**
 * OptimizedImage — serves AVIF → WebP → original for heavy raster assets.
 *
 * The AVIF/WebP siblings are produced by `npm run optimize:images`
 * (scripts/optimize-images.ts), which writes `<name>.avif` and `<name>.webp`
 * next to every source image above 150 KB.
 *
 * For public-directory assets (`src="/troopn-logo.png"`) the sibling URLs are
 * derived automatically. For bundled assets the hashed URLs must be imported
 * and passed explicitly:
 *
 *   import png from "@/assets/hero.png";
 *   import webp from "@/assets/hero.webp";
 *   import avif from "@/assets/hero.avif";
 *   <OptimizedImage src={png} webpSrc={webp} avifSrc={avif} width={1280} height={896} alt="…" />
 *
 * `width`/`height` are required: the intrinsic pixel dimensions let the browser
 * reserve the box before decode, which is what keeps CLS at zero.
 */
import { memo } from "react";
import { PUBLIC_OPTIMIZED_SOURCES } from "@/lib/optimized-image-manifest";

type ImgProps = React.ImgHTMLAttributes<HTMLImageElement>;

export interface OptimizedImageProps extends Omit<ImgProps, "src" | "width" | "height"> {
  /** Fallback source — the original PNG/JPG. */
  src: string;
  /** Explicit AVIF source. Derived from `src` for public-directory paths. */
  avifSrc?: string;
  /** Explicit WebP source. Derived from `src` for public-directory paths. */
  webpSrc?: string;
  alt: string;
  /** Intrinsic width in pixels — required to avoid layout shift. */
  width: number;
  /** Intrinsic height in pixels — required to avoid layout shift. */
  height: number;
  /** Class applied to the wrapping <picture>; `className` goes on the <img>. */
  pictureClassName?: string;
}

/**
 * Public-directory sources have stable, unhashed URLs, so the sibling path is
 * a plain extension swap — but only for the sources the optimizer actually
 * generated derivatives for (see PUBLIC_OPTIMIZED_SOURCES, regenerated by
 * `npm run optimize:images`). Advertising a sibling that does not exist would
 * make supporting browsers pick a 404 over the working original. Bundled
 * imports get content-hashed filenames that cannot be guessed — those callers
 * pass `avifSrc`/`webpSrc` themselves.
 */
export function deriveSiblingSource(src: string, format: "avif" | "webp"): string | undefined {
  if (!PUBLIC_OPTIMIZED_SOURCES.has(src)) return undefined;
  return src.replace(/\.(png|jpe?g)$/i, `.${format}`);
}

function OptimizedImageBase({
  src,
  avifSrc,
  webpSrc,
  alt,
  width,
  height,
  pictureClassName,
  loading = "lazy",
  decoding = "async",
  ...imgProps
}: OptimizedImageProps) {
  const avif = avifSrc ?? deriveSiblingSource(src, "avif");
  const webp = webpSrc ?? deriveSiblingSource(src, "webp");

  return (
    <picture className={pictureClassName}>
      {avif && <source srcSet={avif} type="image/avif" />}
      {webp && <source srcSet={webp} type="image/webp" />}
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        decoding={decoding}
        {...imgProps}
      />
    </picture>
  );
}

export const OptimizedImage = memo(OptimizedImageBase);
